import { supabase } from '../lib/supabase';

export const governmentFilingService = {
  // ============================================================================
  // LODGMENT MANAGEMENT
  // ============================================================================

  async getAllLodgments() {
    const { data, error } = await supabase?.from('government_filing_lodgments')?.select('*')?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      filingType: row?.filing_type,
      filingPeriod: row?.filing_period,
      portalType: row?.portal_type,
      lodgmentStatus: row?.lodgment_status,
      generatedAt: row?.generated_at,
      submittedAt: row?.submitted_at,
      confirmedAt: row?.confirmed_at,
      portalReferenceNumber: row?.portal_reference_number,
      portalResponseData: row?.portal_response_data,
      totalAmount: row?.total_amount,
      employeeCount: row?.employee_count,
      errorMessage: row?.error_message,
      retryCount: row?.retry_count,
      lastRetryAt: row?.last_retry_at,
      createdBy: row?.created_by,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getLodgmentById(id) {
    const { data, error } = await supabase?.from('government_filing_lodgments')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      filingType: data?.filing_type,
      filingPeriod: data?.filing_period,
      portalType: data?.portal_type,
      lodgmentStatus: data?.lodgment_status,
      generatedAt: data?.generated_at,
      submittedAt: data?.submitted_at,
      confirmedAt: data?.confirmed_at,
      portalReferenceNumber: data?.portal_reference_number,
      portalResponseData: data?.portal_response_data,
      totalAmount: data?.total_amount,
      employeeCount: data?.employee_count,
      errorMessage: data?.error_message,
      retryCount: data?.retry_count,
      createdBy: data?.created_by,
      createdAt: data?.created_at
    };
  },

  async getLodgmentsByType(filingType) {
    const { data, error } = await supabase?.from('government_filing_lodgments')?.select('*')?.eq('filing_type', filingType)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      filingPeriod: row?.filing_period,
      lodgmentStatus: row?.lodgment_status,
      submittedAt: row?.submitted_at,
      portalReferenceNumber: row?.portal_reference_number,
      totalAmount: row?.total_amount,
      employeeCount: row?.employee_count
    })) || [];
  },

  async createLodgment(lodgment) {
    const { data, error } = await supabase?.from('government_filing_lodgments')?.insert({
        filing_type: lodgment?.filingType,
        filing_period: lodgment?.filingPeriod,
        portal_type: lodgment?.portalType,
        lodgment_status: lodgment?.lodgmentStatus || 'pending',
        total_amount: lodgment?.totalAmount,
        employee_count: lodgment?.employeeCount,
        created_by: lodgment?.createdBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      filingType: data?.filing_type,
      filingPeriod: data?.filing_period,
      lodgmentStatus: data?.lodgment_status
    };
  },

  async updateLodgmentStatus(id, status, updates = {}) {
    const updateData = {
      lodgment_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'submitted') {
      updateData.submitted_at = new Date()?.toISOString();
      if (updates?.portalReferenceNumber) {
        updateData.portal_reference_number = updates?.portalReferenceNumber;
      }
    } else if (status === 'confirmed') {
      updateData.confirmed_at = new Date()?.toISOString();
    } else if (status === 'failed' || status === 'rejected') {
      updateData.error_message = updates?.errorMessage;
      updateData.retry_count = (updates?.retryCount || 0) + 1;
      updateData.last_retry_at = new Date()?.toISOString();
    }

    if (updates?.portalResponseData) {
      updateData.portal_response_data = updates?.portalResponseData;
    }

    const { data, error } = await supabase?.from('government_filing_lodgments')?.update(updateData)?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      lodgmentStatus: data?.lodgment_status,
      submittedAt: data?.submitted_at,
      confirmedAt: data?.confirmed_at,
      portalReferenceNumber: data?.portal_reference_number
    };
  },

  // ============================================================================
  // AUTO-GENERATION AND SUBMISSION
  // ============================================================================

  async generateNasfundFiling(payrollRunId, filingPeriod) {
    try {
      // Get payroll details for the run
      const { data: payrollDetails, error: detailsError } = await supabase?.from('payroll_details')?.select(`
          *,
          employees (
            id,
            employee_number,
            first_name,
            last_name
          )
        `)?.eq('payroll_run_id', payrollRunId);
      
      if (detailsError) throw detailsError;

      // Get current NASFUND rates
      const { data: rates, error: ratesError } = await supabase?.rpc('get_current_nasfund_rates');
      if (ratesError) throw ratesError;

      const currentRate = rates?.[0];
      if (!currentRate) throw new Error('No current NASFUND rates found');

      // Calculate totals
      let totalEmployeeContribution = 0;
      let totalEmployerContribution = 0;
      const employeeCount = payrollDetails?.length || 0;

      payrollDetails?.forEach(detail => {
        totalEmployeeContribution += parseFloat(detail?.employee_super || 0);
        totalEmployerContribution += parseFloat(detail?.employer_super || 0);
      });

      const totalAmount = totalEmployeeContribution + totalEmployerContribution;

      // Create lodgment record
      const lodgment = await this.createLodgment({
        filingType: 'nasfund',
        filingPeriod: filingPeriod,
        portalType: 'nasfund_portal',
        totalAmount: totalAmount,
        employeeCount: employeeCount,
        createdBy: 'system'
      });

      // Mark as generated
      await supabase?.from('government_filing_lodgments')?.update({
          generated_at: new Date()?.toISOString()
        })?.eq('id', lodgment?.id);

      return {
        ...lodgment,
        totalEmployeeContribution,
        totalEmployerContribution,
        employeeCount
      };
    } catch (error) {
      throw new Error(`Failed to generate NASFUND filing: ${error?.message}`);
    }
  },

  async generateSwtFiling(payrollRunId, filingPeriod) {
    try {
      // Get payroll details for the run
      const { data: payrollDetails, error: detailsError } = await supabase?.from('payroll_details')?.select(`
          *,
          employees (
            id,
            employee_number,
            first_name,
            last_name
          )
        `)?.eq('payroll_run_id', payrollRunId);
      
      if (detailsError) throw detailsError;

      // Calculate totals
      let totalSwt = 0;
      const employeeCount = payrollDetails?.length || 0;

      payrollDetails?.forEach(detail => {
        totalSwt += parseFloat(detail?.swt_amount || 0);
      });

      // Create lodgment record
      const lodgment = await this.createLodgment({
        filingType: 'swt',
        filingPeriod: filingPeriod,
        portalType: 'irc_portal',
        totalAmount: totalSwt,
        employeeCount: employeeCount,
        createdBy: 'system'
      });

      // Mark as generated
      await supabase?.from('government_filing_lodgments')?.update({
          generated_at: new Date()?.toISOString()
        })?.eq('id', lodgment?.id);

      return {
        ...lodgment,
        totalSwt,
        employeeCount
      };
    } catch (error) {
      throw new Error(`Failed to generate SWT filing: ${error?.message}`);
    }
  },

  async submitFilingToPortal(lodgmentId) {
    try {
      // Get lodgment details
      const lodgment = await this.getLodgmentById(lodgmentId);

      // Simulate portal submission (In production, this would call actual government portal APIs)
      const portalReferenceNumber = this.generatePortalReference(lodgment?.filingType, lodgment?.filingPeriod);

      // Update lodgment status to submitted
      const updated = await this.updateLodgmentStatus(lodgmentId, 'submitted', {
        portalReferenceNumber: portalReferenceNumber,
        portalResponseData: {
          submittedAt: new Date()?.toISOString(),
          status: 'submitted',
          message: 'Filing successfully submitted to government portal'
        }
      });

      // Auto-confirm after submission (In production, this would be triggered by portal webhook)
      setTimeout(async () => {
        await this.confirmFiling(lodgmentId, portalReferenceNumber);
      }, 2000);

      return updated;
    } catch (error) {
      // Update status to failed
      await this.updateLodgmentStatus(lodgmentId, 'failed', {
        errorMessage: error?.message
      });
      throw error;
    }
  },

  async confirmFiling(lodgmentId, confirmationNumber) {
    try {
      // Update lodgment to confirmed
      await this.updateLodgmentStatus(lodgmentId, 'confirmed');

      // Create confirmation record
      const { data, error } = await supabase?.from('filing_confirmation_records')?.insert({
          lodgment_id: lodgmentId,
          confirmation_number: confirmationNumber,
          confirmation_date: new Date()?.toISOString(),
          verified_by: 'system',
          verification_notes: 'Filing confirmed by government portal',
          compliance_status: 'compliant'
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        confirmationNumber: data?.confirmation_number,
        confirmationDate: data?.confirmation_date
      };
    } catch (error) {
      throw new Error(`Failed to confirm filing: ${error?.message}`);
    }
  },

  // ============================================================================
  // CONFIRMATION RECORDS
  // ============================================================================

  async getConfirmationRecords() {
    const { data, error } = await supabase?.from('filing_confirmation_records')?.select(`
        *,
        government_filing_lodgments (
          filing_type,
          filing_period,
          portal_type,
          total_amount
        )
      `)?.order('confirmation_date', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      lodgmentId: row?.lodgment_id,
      confirmationNumber: row?.confirmation_number,
      confirmationDate: row?.confirmation_date,
      confirmationDocumentUrl: row?.confirmation_document_url,
      verifiedBy: row?.verified_by,
      verificationNotes: row?.verification_notes,
      complianceStatus: row?.compliance_status,
      metadata: row?.metadata,
      lodgment: row?.government_filing_lodgments ? {
        filingType: row?.government_filing_lodgments?.filing_type,
        filingPeriod: row?.government_filing_lodgments?.filing_period,
        portalType: row?.government_filing_lodgments?.portal_type,
        totalAmount: row?.government_filing_lodgments?.total_amount
      } : null
    })) || [];
  },

  // ============================================================================
  // AUTOMATED SCHEDULES
  // ============================================================================

  async getAutomatedSchedules() {
    const { data, error } = await supabase?.from('automated_filing_schedules')?.select('*')?.order('next_filing_date', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      scheduleName: row?.schedule_name,
      filingType: row?.filing_type,
      frequency: row?.frequency,
      dayOfMonth: row?.day_of_month,
      nextFilingDate: row?.next_filing_date,
      lastFilingDate: row?.last_filing_date,
      autoGenerate: row?.auto_generate,
      autoSubmit: row?.auto_submit,
      isActive: row?.is_active,
      createdBy: row?.created_by,
      createdAt: row?.created_at
    })) || [];
  },

  async createAutomatedSchedule(schedule) {
    const { data, error } = await supabase?.from('automated_filing_schedules')?.insert({
        schedule_name: schedule?.scheduleName,
        filing_type: schedule?.filingType,
        frequency: schedule?.frequency,
        day_of_month: schedule?.dayOfMonth,
        next_filing_date: schedule?.nextFilingDate,
        auto_generate: schedule?.autoGenerate ?? true,
        auto_submit: schedule?.autoSubmit ?? false,
        is_active: schedule?.isActive ?? true,
        created_by: schedule?.createdBy
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      scheduleName: data?.schedule_name,
      filingType: data?.filing_type,
      nextFilingDate: data?.next_filing_date
    };
  },

  async updateAutomatedSchedule(id, schedule) {
    const { data, error } = await supabase?.from('automated_filing_schedules')?.update({
        schedule_name: schedule?.scheduleName,
        frequency: schedule?.frequency,
        day_of_month: schedule?.dayOfMonth,
        next_filing_date: schedule?.nextFilingDate,
        auto_generate: schedule?.autoGenerate,
        auto_submit: schedule?.autoSubmit,
        is_active: schedule?.isActive
      })?.eq('id', id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      scheduleName: data?.schedule_name,
      isActive: data?.is_active
    };
  },

  // ============================================================================
  // RATES AND CALCULATIONS
  // ============================================================================

  async getCurrentNasfundRates() {
    const { data, error } = await supabase?.from('nasfund_rates')?.select('*')?.eq('is_current', true)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      effectiveDate: data?.effective_date,
      employeeRate: data?.employee_rate,
      employerRate: data?.employer_rate,
      notes: data?.notes
    };
  },

  async getCurrentSwtBrackets() {
    const { data, error } = await supabase?.from('swt_tax_brackets')?.select('*')?.eq('is_current', true)?.order('bracket_order', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      bracketOrder: row?.bracket_order,
      incomeFrom: row?.income_from,
      incomeTo: row?.income_to,
      taxRate: row?.tax_rate,
      fixedAmount: row?.fixed_amount
    })) || [];
  },

  // ============================================================================
  // UTILITIES
  // ============================================================================

  generatePortalReference(filingType, filingPeriod) {
    const prefix = filingType === 'nasfund' ? 'NSF' : 'SWT';
    const timestamp = Date.now()?.toString()?.slice(-6);
    const random = Math.floor(Math.random() * 10000)?.toString()?.padStart(4, '0');
    return `${prefix}-${filingPeriod?.replace(/\s+/g, '-')}-${timestamp}-${random}`;
  },

  formatCurrency(amount) {
    return `K${parseFloat(amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

export default governmentFilingService;
